import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchEmirates, updateEmirate, createEmirate, deleteEmirate } from '@/lib/api'
import type { Emirate } from '@/lib/types'

const EMOJI: Record<string, string> = { dubai: '🏙️', 'abu-dhabi': '🏛️', sharjah: '🌆', ajman: '🏘️', 'ras-al-khaimah': '🏖️', fujairah: '🌴', 'umm-al-quwain': '🏝️' }

export default function EmiratesList() {
  const [items, setItems] = useState<Emirate[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newNameAr, setNewNameAr] = useState('')
  const [newSlug, setNewSlug] = useState('')
  const [showNew, setShowNew] = useState(false)

  useEffect(() => { fetchEmirates().then(e => { setItems(e); setLoading(false) }) }, [])

  async function handleUpdate(id: string) {
    const e = items.find(i => i.id === id); if (!e) return
    await updateEmirate(id, { name_en: e.name_en, name_ar: e.name_ar, slug: e.slug })
    setEditing(null)
  }

  async function handleCreate() {
    if (!newName || !newSlug) return
    await createEmirate({ name_en: newName, name_ar: newNameAr || newName, slug: newSlug })
    setShowNew(false); setNewName(''); setNewNameAr(''); setNewSlug('')
    fetchEmirates().then(setItems)
  }

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(`Delete "${name}"?`)) return
    await deleteEmirate(id)
    setItems(prev => prev.filter(e => e.id !== id))
  }

  const action = (
    <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all duration-200 shadow-lg shadow-brand-500/20 hover:shadow-brand-500/30">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
      Add Emirate
    </button>
  )

  return (
    <Layout title="Emirates" action={action}>
      <div className="max-w-4xl mx-auto space-y-5">
        {showNew && (
          <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">New Emirate</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div><label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Name (EN) *</label><input value={newName} onChange={e => setNewName(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" /></div>
              <div><label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Name (AR) *</label><input value={newNameAr} onChange={e => setNewNameAr(e.target.value)} dir="rtl" className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" /></div>
              <div><label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Slug *</label><input value={newSlug} onChange={e => setNewSlug(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" /></div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleCreate} className="bg-brand-600 text-white text-sm font-semibold px-5 py-2 rounded-xl hover:bg-brand-700">Create</button>
              <button onClick={() => setShowNew(false)} className="text-sm font-medium text-slate-600 px-4 py-2 rounded-xl border border-surface-200 hover:bg-surface-50">Cancel</button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-surface-200 shadow-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-50/50">
                <th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Emirate</th>
                <th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Arabic</th>
                <th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Slug</th>
                <th className="text-right px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100">
              {loading ? [1,2,3,4,5,6,7].map(i => (
                <tr key={i} className="animate-pulse"><td className="px-5 py-4"><div className="h-4 w-28 bg-surface-100 rounded" /></td><td className="px-5 py-4"><div className="h-4 w-28 bg-surface-100 rounded" /></td><td className="px-5 py-4"><div className="h-4 w-20 bg-surface-100 rounded" /></td><td className="px-5 py-4"><div className="h-4 w-20 bg-surface-100 rounded ml-auto" /></td></tr>
              )) : items.map(e => (
                <tr key={e.id} className="hover:bg-surface-50/50 transition-colors">
                  <td className="px-5 py-4">
                    {editing === e.id ? (
                      <input value={e.name_en} onChange={ev => setItems(prev => prev.map(x => x.id === e.id ? { ...x, name_en: ev.target.value } : x))} className="w-full px-2 py-1.5 rounded-lg border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
                    ) : (
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <span>{EMOJI[e.slug] ?? '📍'}</span> {e.name_en}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {editing === e.id ? (
                      <input value={e.name_ar} onChange={ev => setItems(prev => prev.map(x => x.id === e.id ? { ...x, name_ar: ev.target.value } : x))} dir="rtl" className="w-full px-2 py-1.5 rounded-lg border border-surface-200 text-sm" />
                    ) : <span className="text-[13px] text-slate-600 font-medium">{e.name_ar}</span>}
                  </td>
                  <td className="px-5 py-4">
                    {editing === e.id ? (
                      <input value={e.slug} onChange={ev => setItems(prev => prev.map(x => x.id === e.id ? { ...x, slug: ev.target.value } : x))} className="w-full px-2 py-1.5 rounded-lg border border-surface-200 text-sm font-mono" />
                    ) : <code className="text-[12px] bg-surface-100 px-2.5 py-1 rounded-lg text-slate-600 font-medium">{e.slug}</code>}
                  </td>
                  <td className="px-5 py-4 text-right">
                    {editing === e.id ? (
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => handleUpdate(e.id)} className="text-[12px] font-semibold text-white bg-brand-600 hover:bg-brand-700 px-3 py-1.5 rounded-lg transition-colors">Save</button>
                        <button onClick={() => setEditing(null)} className="text-[12px] font-medium text-slate-600 hover:bg-surface-100 px-3 py-1.5 rounded-lg transition-colors">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setEditing(e.id)} className="text-[12px] font-medium text-slate-500 hover:text-brand-600 bg-surface-100 hover:bg-brand-50 px-3 py-1.5 rounded-lg transition-colors">Edit</button>
                        <button onClick={() => handleDelete(e.id, e.name_en)} className="text-[12px] font-medium text-slate-500 hover:text-red-600 bg-surface-100 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  )
}
